import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import CircleCIConnectForm from "./CircleCIConnectForm";

const CircleCIIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("circleci");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCircleCIIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCircleCIIntegration(values.api_token.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("CircleCI integrated successfully");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from CircleCI and prioritize the suggested remediation."
            : ""
        }
        image={logos.CircleCILogo}
        iconBorderRadius={0}
        name={"CircleCI"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>CircleCI is integrated!</Typography>
            </Box>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                </Grid>
              </Box>

              <GuidelineStepByStep>
                <GuidelineStep title="Generate a Personal API Token in CircleCI">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Typography variant="body1">
                      Navigate to your CircleCI User Settings to generate a
                      Personal API Token.
                    </Typography>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://app.circleci.com/settings/user/tokens",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none", width: "fit-content" }}
                    >
                      Open CircleCI User Settings
                    </PrimaryButton>
                    <Typography variant="body1">
                      1. Click "Create New Token"
                      <br />
                      2. Provide a descriptive name for the token (e.g., "Breeze
                      Security Platform")
                      <br />
                      3. Click "Add API Token"
                      <br />
                      4. Copy the generated token (it will not be displayed
                      again)
                    </Typography>
                  </Box>
                </GuidelineStep>

                <GuidelineStep id="connectCircleCI" title="Connect CircleCI">
                  <Box width={700}>
                    <CircleCIConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CircleCIIntegration;
